// Reglas de los permisos de trabajo (Fase 0, bloque 5; decisiones de JC del
// 19/10/2026). La base aplica las mismas reglas de activación y cierre con el
// trigger work_permits_reglas_estado (20261019030000).
//
// - Todas las preguntas (catálogo y propias) se responden Cumple / No cumple /
//   No aplica.
// - Un permiso nace 'pendiente'. Se activa con el checklist completo, sin ningún
//   "No cumple", y con las firmas de verificación de quien autoriza y de al
//   menos un trabajador ejecutor.
// - Se cierra con la firma de cierre de quien autoriza (el checklist ya se
//   exigió al activar).
// - GT-11: permiso cerrado, con firma de cierre y checklist completo sin
//   "No cumple".

import { plural } from './plurales';

export type ResultadoPregunta = 'cumple' | 'no_cumple' | 'no_aplica';
export type EtapaFirma = 'verificacion' | 'cierre';
export type RolFirma = 'autoriza' | 'ejecutor';

export const RESULTADO_LABELS: Record<ResultadoPregunta, string> = {
  cumple:    'Cumple',
  no_cumple: 'No cumple',
  no_aplica: 'No aplica',
};

export const ROL_FIRMA_LABELS: Record<RolFirma, string> = {
  autoriza: 'Autoriza',
  ejecutor: 'Trabajador ejecutor',
};

export const HORAS_VIGENCIA_POR_DEFECTO = 24;

export function vigenteHastaPorDefecto(desde: Date): Date {
  return new Date(desde.getTime() + HORAS_VIGENCIA_POR_DEFECTO * 60 * 60 * 1000);
}

// Pulsar el resultado ya elegido lo quita (la pregunta vuelve a quedar sin responder).
export function siguienteResultado(actual: ResultadoPregunta | null, pulsado: ResultadoPregunta): ResultadoPregunta | null {
  return actual === pulsado ? null : pulsado;
}

export interface EstadoChecklist {
  total:       number;
  respondidas: number;
  noCumple:    number;
  completo:    boolean;
  // Checklist completo y sin "No cumple": aparece la sección de firmas.
  listoParaFirmar: boolean;
}

export function estadoChecklist(
  catalogo: { resultado: ResultadoPregunta | null }[],
  propias:  { resultado: ResultadoPregunta | null }[] = [],
): EstadoChecklist {
  const todas = [...catalogo, ...propias];
  const total = todas.length;
  const respondidas = todas.filter(p => p.resultado !== null).length;
  const noCumple = todas.filter(p => p.resultado === 'no_cumple').length;
  // Sin preguntas del catálogo no hay checklist (mismo criterio que el trigger).
  const completo = catalogo.length > 0 && respondidas === total;
  return { total, respondidas, noCumple, completo, listoParaFirmar: completo && noCumple === 0 };
}

export interface FirmaResumen { etapa: EtapaFirma; rol: RolFirma; }

function tieneFirma(firmas: FirmaResumen[], etapa: EtapaFirma, rol: RolFirma): boolean {
  return firmas.some(f => f.etapa === etapa && f.rol === rol);
}

export function faltantesParaActivar(checklist: EstadoChecklist, firmas: FirmaResumen[]): string[] {
  const faltan: string[] = [];
  if (!checklist.completo) faltan.push(`Responder todas las preguntas (${checklist.respondidas}/${checklist.total})`);
  if (checklist.noCumple > 0) faltan.push(`Resolver ${plural(checklist.noCumple, 'pregunta', 'preguntas')} en "No cumple"`);
  if (!tieneFirma(firmas, 'verificacion', 'autoriza')) faltan.push('Firma de quien autoriza');
  if (!tieneFirma(firmas, 'verificacion', 'ejecutor')) faltan.push('Firma de al menos un trabajador ejecutor');
  return faltan;
}

export function faltantesParaCerrar(firmas: FirmaResumen[]): string[] {
  return tieneFirma(firmas, 'cierre', 'autoriza') ? [] : ['Firma de cierre de quien autoriza'];
}

export function cuentaParaGT11(status: string, checklist: EstadoChecklist, firmas: FirmaResumen[]): boolean {
  return status === 'cerrado'
    && tieneFirma(firmas, 'cierre', 'autoriza')
    && checklist.completo
    && checklist.noCumple === 0;
}

// Porcentaje de preguntas respondidas (lista de permisos).
export function porcentajeRespondido(checklist: EstadoChecklist): number {
  return checklist.total > 0 ? Math.round((checklist.respondidas / checklist.total) * 100) : 0;
}
